import { Sparkles, Repeat, Moon } from "lucide-react";

// Scripted walkthroughs for the public desktop-agent demo — the same idea as
// `components/extension/scenarios.js`, but the story here is the round trip:
// dashboard → websocket → tray agent → its own browser → results streaming back.
// Nothing calls /exe; a real device can't be driven from a marketing page.
//
// `point` is the agent pointer's target inside the mock browser, in pixels from
// the viewport box's top-left. The checkout block is top-anchored and the same
// height at every breakpoint, so pixels stay on target where percentages drift.

export const TABS = [
  {
    id: "ai",
    label: "First run",
    icon: Sparkles,
    hint: "The AI works out every step and captures the bug",
  },
  {
    id: "replay",
    label: "Replay",
    icon: Repeat,
    hint: "The same test again — no AI, no tokens, no cost",
  },
  {
    id: "suite",
    label: "Nightly suite",
    icon: Moon,
    hint: "Three tests at 2 AM with nobody at the desk",
  },
];

const CARD = "4242 4242 4242 4242";

const POINT = {
  card: { x: 150, y: 122 },
  expiry: { x: 118, y: 188 },
  pay: { x: 152, y: 232 },
};

const BUG = {
  title: "Payment never completes — Pay hangs silently",
  severity: "high",
  expected: "A receipt appears after paying with a valid test card.",
  actual: "The button spins forever. No receipt and no error message.",
  repro: [
    "Open /checkout with one item in the cart",
    `Enter card ${CARD}, exp 12/29, CVC 311`,
    "Click “Pay $49.00” and wait",
  ],
  evidence: ["POST /checkout/confirm → 500", "console: TypeError line 84", "HAR + 4 screenshots"],
};

export const SCENARIOS = {
  ai: {
    id: "ai",
    title: "Checkout smoke",
    goal: "Pay for the item in the cart with a test card and check the receipt shows.",
    device: "Online · 16 GB · chromium",
    trayTitle: "Run received",
    trayBody: "Checkout smoke · from dashboard",
    runLabel: "Run on this PC",
    ai: true,
    gap: 1350,
    suite: null,
    steps: [
      { label: "Open a clean browser session", act: "boot", ms: "0.9s" },
      { label: "Navigate to /checkout", act: "nav", ms: "1.4s", shot: "load" },
      { label: "Fill “Card number”", act: "type", field: "card", value: CARD, ms: "0.6s", point: POINT.card },
      { label: "Fill “Expiry” and “CVC”", act: "type2", ms: "0.7s", shot: "filled", point: POINT.expiry },
      { label: "Click “Pay $49.00”", act: "click", ms: "0.3s", point: POINT.pay },
      { label: "Wait for the receipt", act: "wait", ms: "10.0s", shot: "hang" },
      { label: "Assert receipt is visible", act: "fail", ms: "—", fail: true, shot: "fail" },
    ],
    result: {
      verdict: "FAILED",
      headline: "Failed · bug captured",
      duration: "27s",
      cost: "$0.042",
      tokens: "1,240 tok",
      free: false,
      bug: BUG,
      compare: true,
    },
  },

  replay: {
    id: "replay",
    title: "Checkout smoke — saved test #14",
    goal: "Replaying the compiled steps. Fixed selectors, no model calls.",
    device: "Online · 16 GB · chromium",
    trayTitle: "Replay starting",
    trayBody: "Saved test #14 · no AI",
    runLabel: "Replay on this PC",
    ai: false,
    gap: 620,
    suite: null,
    steps: [
      { label: "Open a clean browser session", act: "boot", ms: "0.4s" },
      { label: "goto /checkout", act: "nav", ms: "0.7s", shot: "load" },
      { label: "fill #card", act: "type", field: "card", value: CARD, ms: "0.1s", point: POINT.card },
      { label: "fill #exp, #cvc", act: "type2", ms: "0.1s", point: POINT.expiry },
      { label: "click [data-pay]", act: "click", ms: "0.1s", point: POINT.pay },
      { label: "expect receipt visible", act: "pass", ms: "1.2s", shot: "receipt", receipt: true },
    ],
    result: {
      verdict: "PASSED",
      headline: "Passed · 0 tokens",
      duration: "6s",
      cost: "$0.000",
      tokens: "0 tok",
      free: true,
      note: "Saved tests replay from fixed steps — the model is never called, so a replay costs nothing.",
      compare: true,
    },
  },

  suite: {
    id: "suite",
    title: "Nightly smoke · 3 tests",
    goal: "Scheduled for 02:00. The PC is locked and nobody is watching.",
    device: "Online · 16 GB · headless · scheduled",
    trayTitle: "Scheduled suite starting",
    trayBody: "Nightly smoke · 02:00",
    runLabel: "Run suite now",
    ai: false,
    gap: 780,
    suite: ["Login", "Search", "Checkout"],
    steps: [
      { label: "Login — sign in as qa@acme.com", act: "boot", ms: "4.1s", suite: 0 },
      { label: "Login — assert dashboard loads", act: "pass", ms: "1.2s", suite: 0, suiteEnd: "ok", shot: "login" },
      { label: "Search — query “blue jacket”", act: "nav", ms: "2.8s", suite: 1 },
      { label: "Search — assert 12 results", act: "pass", ms: "0.9s", suite: 1, suiteEnd: "ok", shot: "results" },
      { label: "Checkout — pay with test card", act: "type", field: "card", value: CARD, ms: "3.4s", suite: 2, point: POINT.card },
      { label: "Checkout — selector moved, self-healing", act: "heal", ms: "2.1s", suite: 2, ai: true, point: POINT.pay },
      { label: "Checkout — assert receipt visible", act: "fail", ms: "10.0s", fail: true, suite: 2, suiteEnd: "bad", shot: "fail" },
    ],
    result: {
      verdict: "PARTIAL",
      headline: "2 passed · 1 failed",
      duration: "1m 12s",
      cost: "$0.006",
      tokens: "180 tok",
      free: false,
      bug: BUG,
      note: "Only the healed step called the model. The rest replayed for free.",
      compare: false,
    },
  },
};

export const getScenario = (id) => SCENARIOS[id] || SCENARIOS.ai;
